import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Observable,
    Subject,
    ReplaySubject,
    tap,
    ignoreElements,
    catchError,
    mergeMap,
    filter,
    timer,
    take
} from "rxjs";
import { webSocket } from "rxjs/webSocket";
import { createContainer } from "unstated-next";

type TivaApp = {
    appId: string;
    accessToken: string;
    organizationId: string;
    tivaDataUrl: string;
    userId: string;
};

type WSRequest = {
    type: string;
    method: string;
    rpcId: string;
    params: unknown;
};

type WSResponse = {
    type: string;
    rpcId: string;
    channelUuid: string;
    completed?: boolean;
    error?: any;
    result?: any;
    userInfo?: any;
};

const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key: string, value: unknown) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
                try {
                    JSON.stringify(value);
                    return value;
                } catch (e) {
                    return;
                }
            }
            seen.add(value);
        }
        return value;
    };
};

function useTivaDataWebSocket() {
    const [tivaApp, setTivaApp] = useState<TivaApp>();
    const [isAuth, setIsAuth] = useState<boolean>(false);
    const webSocketUrl = useMemo(
        () =>
            tivaApp?.tivaDataUrl.replace("http", "ws") ??
            process.env.REACT_APP_TIVA_DATA_WEBSOCKET_URL ??
            "",
        [tivaApp?.tivaDataUrl]
    );
    const accessToken = useMemo(() => tivaApp?.accessToken, [tivaApp?.accessToken]);
    const dispatcher = useMemo(() => new Subject<WSRequest>(), []);
    const wsMessagesSubject = useMemo(() => new ReplaySubject<WSResponse>(), []);

    const pipeClientServerMessages$ = useCallback((): Observable<WSRequest | WSResponse> => {
        const webSocketServer = webSocket(webSocketUrl);

        dispatcher
            .pipe(
                tap(action => {
                    webSocketServer.next(JSON.parse(JSON.stringify(action, getCircularReplacer())));
                }),
                ignoreElements()
            )
            .subscribe();

        return webSocketServer.pipe(
            catchError(() => {
                console.log(`web socket connection error, try to reconnect to ${webSocketUrl}`);
                return timer(1000).pipe(
                    mergeMap(() => pipeClientServerMessages$()),
                    ignoreElements()
                );
            }),
            tap((action: any) => {
                if (action as WSResponse) {
                    wsMessagesSubject.next(action);
                }
            })
        );
    }, [webSocketUrl, dispatcher, wsMessagesSubject]);

    const rpc$ = useCallback(
        (method: string, params: object) =>
            new Observable(observer => {
                const action: WSRequest = {
                    type: "RPC",
                    rpcId: crypto.randomUUID(),
                    method,
                    params
                };
                wsMessagesSubject
                    .pipe(
                        filter(({ rpcId, type }) => action.rpcId === rpcId && method === type),
                        take(1)
                    )
                    .subscribe({
                        next: (response: WSResponse) => {
                            const { error, result } = response;
                            if (error || result?.error) {
                                observer.error(error ?? result?.error);
                            } else {
                                observer.next(result);
                            }
                        },
                        complete: () => observer.complete()
                    });
                dispatcher.next(action);
            }),
        [dispatcher, wsMessagesSubject]
    );

    useEffect(() => {
        if (accessToken) {
            const authSub = wsMessagesSubject
                .pipe(
                    filter(item => item.type === "HANDSHAKEN"),
                    mergeMap(() => {
                        return rpc$("AUTHENTICATE", { token: accessToken });
                    })
                )
                .subscribe({
                    next: () => {
                        setIsAuth(true);
                        console.log("~~~ tiva data authenticated ~~~");
                    },
                    error: error => {
                        setIsAuth(false);
                        console.log("tiva data authentication error", error?.message);
                    }
                });

            const wsSub = pipeClientServerMessages$().subscribe();

            return () => {
                authSub.unsubscribe();
                wsSub.unsubscribe();
            };
        }
    }, [accessToken, wsMessagesSubject, pipeClientServerMessages$, rpc$]);

    return {
        tivaApp,
        isAuth,
        rpc$,
        setTivaApp
    };
}

export const TivaDataWebSocketContainer = createContainer(useTivaDataWebSocket);
